import {Button, Form, Input, Modal} from "antd";
import {forwardRef, useImperativeHandle, useState} from "react";
import {useTranslation} from "react-i18next";
import {AppValidator} from "@/common/app-validate.ts";
import {UserService} from "@/services/user/UserService.ts";
import {toast} from "@/utils/toast.tsx";

export interface ChangePasswordModalRef {
    openModal: () => void,
    closeModal: () => void,
}

const ChangePasswordModal = forwardRef<ChangePasswordModalRef | undefined, any>((_props, ref) => {
    const {t} = useTranslation();
    const validators = AppValidator(t);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [changePasswordForm] = Form.useForm();

    useImperativeHandle(ref, () => ({
        openModal: () => setIsOpen(true),
        closeModal: () => handleCancel(),
    }));

    const handleOk = () => {
        changePasswordForm.validateFields().then(async (values: ChangePasswordRequest) => {
            const res: SuccessResponse | null | undefined = await UserService.changePassword(values);
            if (res) {
                toast.success(res.message || "Success");
                setIsOpen(false);
                changePasswordForm.resetFields();
            }
        })
    };

    const handleCancel = () => {
        setIsOpen(false);
        changePasswordForm.resetFields();
    };

    return (
        <>
            <Modal
                title={<h2>{t('title.changePassword')}</h2>}
                open={isOpen}
                footer={<>
                    <Button
                        type="default"
                        className="mx-1"
                        size={'large'}
                        onClick={() => handleCancel()}
                    >
                        {t('button.cancel')}
                    </Button>
                    <Button
                        type="primary"
                        size={'large'}
                        className="mx-1"
                        onClick={handleOk}
                    >
                        {t('button.save')}
                    </Button>

                </>}
                onCancel={handleCancel}
            >
                <Form
                    form={changePasswordForm}
                    name="user-info-form"
                    layout={"vertical"}
                    onFinish={handleOk}
                    size={'large'}
                >
                    <Form.Item
                        name="oldPassword"
                        label={t('field.oldPassword')}
                        rules={[
                            validators.required(t('field.oldPassword')),
                            validators.minLength(8, t('field.oldPassword')),
                        ]}
                    >
                        <Input.Password
                            max={255}
                            placeholder={t('placeholder.enterYourOldPassword')}
                        />
                    </Form.Item>
                    <Form.Item
                        name="newPassword"
                        label={t('field.newPassword')}
                        rules={[
                            validators.required(t('field.newPassword')),
                            validators.minLength(8, t('field.newPassword')),
                            validators.validatePasswordStrength(),
                            ({getFieldValue}) => validators.confirmNewPassNotIncludeOldPass('oldPassword', t('message.newPassIncludeOldPass'), getFieldValue)
                        ]}
                    >
                        <Input.Password
                            max={255}
                            placeholder={t('placeholder.enterYourNewPassword')}
                        />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label={t('field.confirmPassword')}
                        rules={[
                            validators.required(t('field.confirmPassword')),
                            ({getFieldValue}) => validators.confirmPasswordMatch('newPassword', t('message.passwordNotMatch'), getFieldValue)
                        ]}
                    >
                        <Input.Password
                            max={255}
                            placeholder={t('placeholder.enterYourConfirmPassword')}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
})

export default ChangePasswordModal;